import { useMutation, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { consolidationContract } from "@common/contracts/consolidation.contract";
import {
    ConsolidationBasisOptionsBody,
    ConsolidationBasisOptionsResult,
    ConsolidationEligibilityBody,
    ConsolidationEligibilityResult,
    CanvasLayoutPositions,
    ConsolidationCanvasLayoutResponse,
    ConsolidationGroupResponse,
    ConsolidationRelationshipResponse,
    CreateConsolidationGroupBody,
    CreateConsolidationRelationshipBody,
    UpdateConsolidationRelationshipBody,
} from "@common/schemas/domains/accounting/consolidation.schema";
import { queryClient } from "queryClient";
import {
    consolidationQueryKeys,
    invalidateConsolidationGroupMutations,
} from "./consolidationCache.helpers";

const consolidationClient = getBackendAPIClient(consolidationContract);

export { consolidationQueryKeys } from "./consolidationCache.helpers";
export { useUpdateGroupConfigMutation } from "./updateConsolidationGroupConfig.api";
export { useUpdateGroupMemberMutation } from "./updateConsolidationGroupMember.api";

function invalidateConsolidation() {
    invalidateConsolidationGroupMutations(queryClient);
}

async function getRelationships(
    workspaceId: string,
): Promise<ConsolidationRelationshipResponse[]> {
    const result = await consolidationClient.listRelationships({
        params: { workspaceId },
        query: {},
    });
    if (result.status !== 200) {
        throw new Error("Failed to load relationships");
    }
    return result.body.relationships;
}

export function useConsolidationRelationships(workspaceId: string | null) {
    return useQuery({
        queryKey: consolidationQueryKeys.relationships(workspaceId ?? ""),
        queryFn: () => getRelationships(workspaceId ?? ""),
        enabled: !!workspaceId,
    });
}

async function getGroups(
    workspaceId: string,
): Promise<ConsolidationGroupResponse[]> {
    const result = await consolidationClient.getGroups({
        params: { workspaceId },
    });
    if (result.status !== 200) {
        throw new Error("Failed to load consolidation groups");
    }
    return result.body.groups;
}

export function useConsolidationGroups(workspaceId: string | null) {
    return useQuery({
        queryKey: consolidationQueryKeys.groups(workspaceId ?? ""),
        queryFn: () => getGroups(workspaceId ?? ""),
        enabled: !!workspaceId,
    });
}

async function getCanvasLayout(
    workspaceId: string,
): Promise<ConsolidationCanvasLayoutResponse> {
    const result = await consolidationClient.getCanvasLayout({
        params: { workspaceId },
    });
    if (result.status !== 200) {
        throw new Error("Failed to load canvas layout");
    }
    return result.body;
}

export function useCanvasLayout(workspaceId: string | null) {
    return useQuery({
        queryKey: consolidationQueryKeys.canvasLayout(workspaceId ?? ""),
        queryFn: () => getCanvasLayout(workspaceId ?? ""),
        enabled: !!workspaceId,
    });
}

export function useSaveCanvasLayoutMutation(workspaceId: string) {
    return useMutation({
        mutationFn: async (
            positions: CanvasLayoutPositions,
        ): Promise<ConsolidationCanvasLayoutResponse> => {
            const result = await consolidationClient.updateCanvasLayout({
                params: { workspaceId },
                body: { positions },
            });
            if (result.status !== 200) {
                throw result;
            }
            return result.body;
        },
        // Write the server's response straight into the cache rather than
        // invalidating: a debounced save must not trigger a refetch that could
        // race the next drag and snap nodes back to their previous positions.
        onSuccess: (layout) => {
            queryClient.setQueryData(
                consolidationQueryKeys.canvasLayout(workspaceId),
                layout,
            );
        },
        // A failed save leaves the optimistic cache ahead of the server; pull
        // the server's truth back so the two don't diverge silently.
        onError: () => {
            void queryClient.invalidateQueries({
                queryKey: consolidationQueryKeys.canvasLayout(workspaceId),
            });
        },
    });
}

export function useResetCanvasLayoutMutation(workspaceId: string) {
    return useMutation({
        mutationFn: async (): Promise<void> => {
            const result = await consolidationClient.resetCanvasLayout({
                params: { workspaceId },
            });
            if (result.status !== 200) {
                throw result;
            }
        },
        onSuccess: () => {
            queryClient.setQueryData<ConsolidationCanvasLayoutResponse>(
                consolidationQueryKeys.canvasLayout(workspaceId),
                { workspaceId, positions: {}, updatedAt: null },
            );
        },
    });
}

export function getConsolidationErrorMessage(
    error: unknown,
    fallback: string,
): string {
    // The backend client throws AxiosError on non-2xx; the API message
    // lives at error.response.data.message.
    if (isAxiosError(error)) {
        const message: unknown = error.response?.data?.message;
        if (typeof message === "string") {
            return message;
        }
        if (Array.isArray(message)) {
            return message.join("\n");
        }
    }
    if (error instanceof Error) {
        return error.message;
    }
    return fallback;
}

interface MutationCallbacks {
    onSuccess?: () => void;
    onError?: (message: string) => void;
}

export function useCreateRelationshipMutation(
    workspaceId: string,
    callbacks?: MutationCallbacks,
) {
    return useMutation({
        mutationFn: async (
            body: CreateConsolidationRelationshipBody,
        ): Promise<ConsolidationRelationshipResponse> => {
            const result = await consolidationClient.createRelationship({
                params: { workspaceId },
                body,
                // the form pane surfaces the error inline
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
            });
            if (result.status !== 201) {
                throw result;
            }
            return result.body.relationship;
        },
        onSuccess: () => {
            invalidateConsolidation();
            callbacks?.onSuccess?.();
        },
        onError: (error) =>
            callbacks?.onError?.(
                getConsolidationErrorMessage(
                    error,
                    "Failed to create ownership relationship",
                ),
            ),
    });
}

interface UpdateRelationshipVariables {
    id: string;
    body: UpdateConsolidationRelationshipBody;
}

export function useUpdateRelationshipMutation(
    workspaceId: string,
    callbacks?: MutationCallbacks,
) {
    return useMutation({
        mutationFn: async ({
            id,
            body,
        }: UpdateRelationshipVariables): Promise<ConsolidationRelationshipResponse> => {
            const result = await consolidationClient.updateRelationship({
                params: { workspaceId, id },
                body,
                // the form pane surfaces the error inline
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
            });
            if (result.status !== 200) {
                throw result;
            }
            return result.body.relationship;
        },
        onSuccess: () => {
            invalidateConsolidation();
            callbacks?.onSuccess?.();
        },
        onError: (error) =>
            callbacks?.onError?.(
                getConsolidationErrorMessage(
                    error,
                    "Failed to update ownership relationship",
                ),
            ),
    });
}

export function useDeleteRelationshipMutation(
    workspaceId: string,
    callbacks?: MutationCallbacks,
) {
    return useMutation({
        mutationFn: async (id: string): Promise<void> => {
            const result = await consolidationClient.deleteRelationship({
                params: { workspaceId, id },
            });
            if (result.status !== 200) {
                throw result;
            }
        },
        onSuccess: () => {
            invalidateConsolidation();
            callbacks?.onSuccess?.();
        },
        onError: (error) =>
            callbacks?.onError?.(
                getConsolidationErrorMessage(
                    error,
                    "Failed to delete ownership relationship",
                ),
            ),
    });
}

interface ReplaceOwnershipRelationshipsInput {
    workspaceId: string;
    createBody: CreateConsolidationRelationshipBody;
    conflictingRelationships: ConsolidationRelationshipResponse[];
}

function toCreateRelationshipBody(
    relationship: ConsolidationRelationshipResponse,
): CreateConsolidationRelationshipBody {
    return {
        type: relationship.type,
        sourceEntityUuid: relationship.sourceEntityUuid,
        targetEntityUuid: relationship.targetEntityUuid,
        ...(relationship.ownershipPercentage !== null
            ? { ownershipPercentage: relationship.ownershipPercentage }
            : {}),
        ...(relationship.startDate !== null
            ? { startDate: relationship.startDate }
            : {}),
        ...(relationship.endDate !== null
            ? { endDate: relationship.endDate }
            : {}),
        ...(relationship.metadata !== null
            ? { metadata: relationship.metadata }
            : {}),
    };
}

/**
 * Best-effort rollback after a failed replace: this is not atomic, so each step
 * is guarded and logged independently and never masks the original error.
 * Deletes the just-created edge first (so we never restore a two-parent state),
 * then re-creates the conflicting edges that were already removed.
 */
async function rollbackReplaceOwnershipRelationships({
    workspaceId,
    createdRelationshipId,
    deletedRelationships,
}: {
    workspaceId: string;
    createdRelationshipId: string;
    deletedRelationships: ConsolidationRelationshipResponse[];
}): Promise<void> {
    try {
        const deleteResult = await consolidationClient.deleteRelationship({
            params: { workspaceId, id: createdRelationshipId },
            extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
        });
        if (deleteResult.status !== 200) {
            // eslint-disable-next-line no-console
            console.error(
                "Failed to roll back created ownership relationship",
                deleteResult,
            );
        }
    } catch (rollbackError) {
        // eslint-disable-next-line no-console
        console.error(
            "Failed to roll back created ownership relationship",
            rollbackError,
        );
    }

    for (const relationship of deletedRelationships) {
        try {
            const recreateResult = await consolidationClient.createRelationship(
                {
                    params: { workspaceId },
                    body: toCreateRelationshipBody(relationship),
                    extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
                },
            );
            if (recreateResult.status !== 201) {
                // eslint-disable-next-line no-console
                console.error(
                    "Failed to restore deleted ownership relationship during rollback",
                    recreateResult,
                );
            }
        } catch (rollbackError) {
            // eslint-disable-next-line no-console
            console.error(
                "Failed to restore deleted ownership relationship during rollback",
                rollbackError,
            );
        }
    }
}

/**
 * Creates the new ownership edge first, then deletes conflicting edges. If a
 * delete fails, best-effort rolls back both the created edge and any conflicting
 * edges already deleted so the target is not left with the wrong owner set.
 * Note this is not transactional; a true atomic replace needs a backend endpoint.
 */
export async function replaceOwnershipRelationships({
    workspaceId,
    createBody,
    conflictingRelationships,
}: ReplaceOwnershipRelationshipsInput): Promise<ConsolidationRelationshipResponse> {
    const createdResult = await consolidationClient.createRelationship({
        params: { workspaceId },
        body: createBody,
        extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
    });
    if (createdResult.status !== 201) {
        throw createdResult;
    }
    const createdRelationship = createdResult.body.relationship;

    const deletedRelationships: ConsolidationRelationshipResponse[] = [];
    try {
        for (const relationship of conflictingRelationships) {
            const deleteResult = await consolidationClient.deleteRelationship({
                params: { workspaceId, id: relationship.id },
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
            });
            if (deleteResult.status !== 200) {
                throw deleteResult;
            }
            deletedRelationships.push(relationship);
        }
    } catch (error) {
        await rollbackReplaceOwnershipRelationships({
            workspaceId,
            createdRelationshipId: createdRelationship.id,
            deletedRelationships,
        });
        throw error;
    } finally {
        invalidateConsolidation();
    }

    return createdRelationship;
}

export function useCheckEligibilityMutation(workspaceId: string) {
    return useMutation({
        mutationFn: async (
            body: ConsolidationEligibilityBody,
        ): Promise<ConsolidationEligibilityResult> => {
            const result = await consolidationClient.checkEligibility({
                params: { workspaceId },
                body,
                // callers render a custom eligibility toast instead of the standard one
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
            });
            if (result.status !== 200) {
                throw result;
            }
            return result.body;
        },
    });
}

export function useGetConsolidationBasisOptionsMutation(workspaceId: string) {
    return useMutation({
        mutationFn: async (
            body: ConsolidationBasisOptionsBody,
        ): Promise<ConsolidationBasisOptionsResult> => {
            const result = await consolidationClient.getBasisOptions({
                params: { workspaceId },
                body,
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
            });
            if (result.status !== 200) {
                throw result;
            }
            return result.body;
        },
    });
}

export function useCreateGroupMutation(
    workspaceId: string,
    callbacks?: MutationCallbacks,
) {
    return useMutation({
        mutationFn: async (
            body: CreateConsolidationGroupBody,
        ): Promise<ConsolidationGroupResponse> => {
            const result = await consolidationClient.createGroup({
                params: { workspaceId },
                body,
            });
            if (result.status !== 201) {
                throw result;
            }
            return result.body.group;
        },
        onSuccess: () => {
            invalidateConsolidation();
            callbacks?.onSuccess?.();
        },
        onError: (error) =>
            callbacks?.onError?.(
                getConsolidationErrorMessage(
                    error,
                    "Failed to create consolidation group",
                ),
            ),
    });
}

export function useDissolveGroupMutation(
    workspaceId: string,
    callbacks?: MutationCallbacks,
) {
    return useMutation({
        mutationFn: async (groupId: string): Promise<void> => {
            const result = await consolidationClient.dissolveGroup({
                params: { workspaceId, id: groupId },
            });
            if (result.status !== 200) {
                throw result;
            }
        },
        onSuccess: () => {
            invalidateConsolidation();
            callbacks?.onSuccess?.();
        },
        onError: (error) =>
            callbacks?.onError?.(
                getConsolidationErrorMessage(
                    error,
                    "Failed to dissolve consolidation group",
                ),
            ),
    });
}
